import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductGrid = ({ products, onEdit, onDelete }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'inactive': return 'text-muted-foreground bg-muted/10';
      case 'out_of_stock': return 'text-danger bg-danger/10';
      case 'discontinued': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusLabel = (status) => {
    return status?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  if (!products || products?.length === 0) {
    return (
      <div className="bg-card rounded-lg p-12 border border-border text-center">
        <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Products Found</h3>
        <p className="text-muted-foreground mb-4">
          Start building your product catalog by adding your first product.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {products?.map((product) => (
        <div
          key={product?.id}
          className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-smooth"
        >
          {/* Product Image */}
          <div className="aspect-square bg-muted flex items-center justify-center">
            {product?.image_url ? (
              <img
                src={product?.image_url}
                alt={product?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="Package" size={48} className="text-muted-foreground" />
            )}
          </div>

          {/* Product Info */}
          <div className="p-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(product?.status)}`}>
                {getStatusLabel(product?.status)}
              </span>
              {product?.track_inventory && product?.stock_quantity <= product?.low_stock_threshold && (
                <span className="text-xs font-medium px-2 py-1 rounded bg-warning/10 text-warning">
                  Low Stock
                </span>
              )}
            </div>

            {/* Product Name */}
            <h3 className="text-lg font-semibold text-foreground mb-1 line-clamp-2">
              {product?.name}
            </h3>

            {/* Category */}
            {product?.product_categories && (
              <p className="text-sm text-muted-foreground mb-2">
                {product?.product_categories?.name}
              </p>
            )}

            {/* SKU */}
            <p className="text-xs text-muted-foreground mb-3">
              SKU: {product?.sku}
            </p>

            {/* Price and Stock */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-2xl font-bold text-primary">
                  ${product?.base_price?.toFixed(2)}
                </p>
                {product?.tax_rate > 0 && (
                  <p className="text-xs text-muted-foreground">
                    +{product?.tax_rate}% tax
                  </p>
                )}
              </div>
              {product?.track_inventory && (
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">
                    {product?.stock_quantity} in stock
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Min: {product?.low_stock_threshold}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => onEdit?.(product)}
              >
                <Icon name="Edit" size={16} className="mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                className="text-danger hover:bg-danger/10"
                onClick={() => onDelete?.(product?.id)}
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductGrid;